/**
 * Where targets.ts is for DX, this file is for Zod validation.
 * It defines the actual Zod schemas for the targets and validates them.
 * It is used to ensure that the targets are correctly defined and can be
 * used in the Axogen config.
 */

import * as z from "zod";
import {
    type Json5TargetOptions,
    type JsonTargetOptions,
    type JsoncTargetOptions,
    type HjsonTargetOptions,
    type YamlTargetOptions,
    type IniTargetOptions,
    type PropertiesTargetOptions,
    type XmlTargetOptions,
    type CsvTargetOptions,
    templateTargetEngines,
} from "./targets.ts";

export const backupTargetOptionsSchema = z.object({
    enabled: z
        .boolean()
        .describe("Whether to create a backup of the target file")
        .default(true)
        .optional(),
    folder: z
        .string()
        .describe(
            "The path to the backup folder. Defaults to '.axogen/backup/{{path}}'"
        )
        .default("")
        .optional(),
    maxBackups: z
        .number()
        .describe("The maximum number of backups to keep. Defaults to 5")
        .default(5)
        .optional(),
    onConflict: z
        .enum(["overwrite", "increment", "skip", "fail"])
        .describe(
            "What to do when a backup file already exists. Defaults to 'increment'. Mostly not used since filenames are ISO timestamped (so pretty unique)."
        )
        .default("increment")
        .optional(),
});
export const backupTargetSchema = z
    .union([
        z.boolean().describe("Whether to create a backup of the target file"),
        backupTargetOptionsSchema,
    ])
    .default(false);

const baseTargetSchema = z.object({
    path: z.string().describe("The output path for the target"),
    schema: z
        .custom<z.ZodType>((val) => {
            return (
                val &&
                typeof val === "object" &&
                ("_def" in val || "_zod" in val)
            );
        })
        .describe("The Schema to validate the variables against")
        .optional(),
    generate_meta: z
        .boolean()
        .describe("Whether to generate metadata for the target")
        .default(false),
    condition: z
        .boolean()
        .describe("Condition to determine if the target should be generated")
        .default(true)
        .optional(),
    backup: backupTargetSchema.optional(),
});

export const jsonTargetSchema = baseTargetSchema.extend({
    type: z
        .literal("json")
        .describe("The type of the target, in this case JSON"),
    variables: z
        .record(z.string(), z.any())
        .describe("Variables to be used in the target"),
    options: z.custom<JsonTargetOptions>().optional(),
});

export const json5TargetSchema = baseTargetSchema.extend({
    type: z
        .literal("json5")
        .describe("The type of the target, in this case JSON5"),
    variables: z
        .record(z.string(), z.any())
        .describe("Variables to be used in the target"),
    options: z.custom<Json5TargetOptions>().optional(),
});

export const jsoncTargetSchema = baseTargetSchema.extend({
    type: z
        .literal("jsonc")
        .describe("The type of the target, in this case JSONC"),
    variables: z
        .record(z.string(), z.any())
        .describe("Variables to be used in the target"),
    options: z.custom<JsoncTargetOptions>().optional(),
});

export const hjsonTargetSchema = baseTargetSchema.extend({
    type: z
        .literal("hjson")
        .describe("The type of the target, in this case HJSON"),
    variables: z
        .record(z.string(), z.any())
        .describe("Variables to be used in the target"),
    options: z.custom<HjsonTargetOptions>().optional(),
});

export const yamlTargetSchema = baseTargetSchema.extend({
    type: z
        .literal("yaml")
        .describe("The type of the target, in this case YAML"),
    variables: z
        .record(z.string(), z.any())
        .describe("Variables to be used in the target"),
    options: z.custom<YamlTargetOptions>().optional(),
});

export const tomlTargetSchema = baseTargetSchema.extend({
    type: z
        .literal("toml")
        .describe("The type of the target, in this case TOML"),
    variables: z
        .record(z.string(), z.any())
        .describe("Variables to be used in the target"),
});

export const iniTargetSchema = baseTargetSchema.extend({
    type: z.literal("ini").describe("The type of the target, in this case INI"),
    variables: z
        .record(z.string(), z.any())
        .describe("Variables to be used in the target"),
    options: z.custom<IniTargetOptions>().optional(),
});

export const propertiesTargetSchema = baseTargetSchema.extend({
    type: z
        .literal("properties")
        .describe("The type of the target, in this case Properties"),
    variables: z
        .record(z.string(), z.any())
        .describe("Variables to be used in the target"),
    options: z.custom<PropertiesTargetOptions>().optional(),
});

export const envTargetSchema = baseTargetSchema.extend({
    type: z
        .literal("env")
        .describe("The type of the target, in this case Environment Variables"),
    variables: z
        .record(z.string(), z.any())
        .describe("Variables to be used in the target"),
});

export const xmlTargetSchema = baseTargetSchema.extend({
    type: z.literal("xml").describe("The type of the target, in this case XML"),
    variables: z
        .record(z.string(), z.any())
        .describe("Variables to be used in the target"),
    options: z.custom<XmlTargetOptions>().optional(),
});

export const csvTargetSchema = baseTargetSchema.extend({
    type: z.literal("csv").describe("The type of the target, in this case CSV"),
    variables: z
        .array(z.record(z.string(), z.any()))
        .describe("Variables to be used in the target"),
    options: z.custom<CsvTargetOptions>().optional(),
});

export const csonTargetSchema = baseTargetSchema.extend({
    type: z
        .literal("cson")
        .describe("The type of the target, in this case CSON"),
    variables: z
        .record(z.string(), z.any())
        .describe("Variables to be used in the target"),
});

export const templateTargetSchema = baseTargetSchema.extend({
    type: z
        .literal("template")
        .describe("The type of the target, in this case Template"),
    engine: z
        .enum(templateTargetEngines)
        .describe("The template engine to use")
        .default("nunjucks"),
    template: z.string().describe("The template string or file path"),
    variables: z
        .record(z.string(), z.any())
        .describe("Variables to be used in the target"),
});

export const anyTargetSchema = z
    .discriminatedUnion("type", [
        jsonTargetSchema,
        json5TargetSchema,
        jsoncTargetSchema,
        hjsonTargetSchema,
        yamlTargetSchema,
        tomlTargetSchema,
        iniTargetSchema,
        propertiesTargetSchema,
        envTargetSchema,
        xmlTargetSchema,
        csvTargetSchema,
        csonTargetSchema,
        templateTargetSchema,
    ])
    .check((ctx) => {
        if (ctx.value.schema && ctx.value.variables) {
            const result = ctx.value.schema.safeParse(ctx.value.variables);
            if (!result.success) {
                result.error.issues.forEach((issue) => {
                    ctx.issues.push({
                        code: "custom",
                        message: issue.message,
                        path: ["variables", ...issue.path],
                        input: ctx.value,
                    });
                });
            }
        }
    });

export const allTargetsSchema = z
    .record(z.string(), anyTargetSchema)
    .describe(
        "A record of targets where the key is the target name and the value is the target definition"
    );

// ---- Exported Types ----

export type ZodBackupTargetOptions = z.infer<typeof backupTargetOptionsSchema>;
export type ZodBackupTarget = z.infer<typeof backupTargetSchema>;

export type ZodAnyTarget = z.infer<typeof anyTargetSchema>;
export type ZodAllTargets = z.infer<typeof allTargetsSchema>;

export type ZodJsonTarget = z.infer<typeof jsonTargetSchema>;
export type ZodJson5Target = z.infer<typeof json5TargetSchema>;
export type ZodJsoncTarget = z.infer<typeof jsoncTargetSchema>;
export type ZodHjsonTarget = z.infer<typeof hjsonTargetSchema>;
export type ZodYamlTarget = z.infer<typeof yamlTargetSchema>;
export type ZodTomlTarget = z.infer<typeof tomlTargetSchema>;
export type ZodIniTarget = z.infer<typeof iniTargetSchema>;
export type ZodPropertiesTarget = z.infer<typeof propertiesTargetSchema>;
export type ZodEnvTarget = z.infer<typeof envTargetSchema>;
export type ZodXmlTarget = z.infer<typeof xmlTargetSchema>;
export type ZodCsvTarget = z.infer<typeof csvTargetSchema>;
export type ZodCsonTarget = z.infer<typeof csonTargetSchema>;
export type ZodTemplateTarget = z.infer<typeof templateTargetSchema>;
